// Generalized component configuration - matches backend ComponentConfigDTO
export interface ComponentConfig {
  id: string;
  type: 'text' | 'date' | 'checkbox' | 'radio' | 'number' | 'sectionHeader' | 'dropdown' | 'metadataDropdown' | 'sql' | 'grid' | 'dynamicGrid';
  label: string;
  description?: string;
  required?: boolean;
  visible?: boolean;
  disabled?: boolean;
  defaultValue?: any;
  value?: any;
  validation?: ValidationRule[];
  /**
   * Component-specific configuration options
   * This field allows for flexible component configuration without requiring
   * separate interfaces for each component type
   */
  additionalOptions?: Record<string, any>;
}

// Legacy interface names for backwards compatibility
export type TextFieldConfig = ComponentConfig & { type: 'text' };
export type DateFieldConfig = ComponentConfig & { type: 'date' };
export type CheckboxConfig = ComponentConfig & { type: 'checkbox' };
export type DropdownConfig = ComponentConfig & { type: 'dropdown' };
export type MetadataDropdownConfig = ComponentConfig & { type: 'metadataDropdown'; metadataType: string };
export type SQLEditorConfig = ComponentConfig & { type: 'sql' };
export type DataGridConfig = ComponentConfig & { type: 'grid' };

// Type-safe helper interfaces for component-specific options
export interface TextFieldOptions {
  placeholder?: string;
  maxLength?: number;
  minLength?: number;
  formatMask?: string;
  multiline?: boolean;
  rows?: number;
}

export interface DateFieldOptions {
  format?: string;
  showTime?: boolean;
  minDate?: string;
  maxDate?: string;
}

export interface CheckboxOptions {
  checkedValue?: any;
  uncheckedValue?: any;
  /**
   * Components to enable/disable based on this checkbox state
   * When checked, these components will be enabled; when unchecked, they will be disabled
   */
  enableComponents?: string[];
  /**
   * Components to disable/enable based on this checkbox state (inverse of enableComponents)
   * When checked, these components will be disabled; when unchecked, they will be enabled
   */
  disableComponents?: string[];
  /**
   * Components to clear/reset based on this checkbox state
   * When checked, these components will be cleared to their default values
   */
  clearComponents?: string[];
}

export interface DropdownOptions {
  options?: DropdownOption[];  // Make static options optional
  multiple?: boolean;
  searchable?: boolean;
  allowClear?: boolean;
  
  // Dynamic loading options
  dynamicLoad?: boolean;        // Enable backend fetching
  transformType?: string;       // Transform type for API call
  componentId?: string;         // Component ID for API call
  dependsOn?: string[];         // Component IDs that trigger refresh
  
  // Cross-field validation options
  validateAgainst?: string;     // Component ID to validate against
  validationMessage?: string;   // Custom validation error message
}

export interface MetadataDropdownOptions {
  metadataType: string;
  searchable?: boolean;
  allowClear?: boolean;
  placeholder?: string;
}

export interface SQLEditorOptions {
  height?: string;
  showToolbar?: boolean;
  showGetSelectButton?: boolean;
  tableName?: string;
}

export interface DataGridOptions {
  columns: GridColumn[];
  showRowNumbers?: boolean;
  allowRowSelection?: boolean;
  showToolbar?: boolean;
  showFooter?: boolean;
  maxHeight?: string;
  minRows?: number;
  maxRows?: number;
  defaultRowData?: Partial<GridRow>;
  emptyText?: string;
  lockedColumns?: number; // Number of columns to lock/pin to the left (default: 2)
}

export interface DynamicGridOptions {
  sourceGridId: string; // ID of the grid that defines the column schema
  columnNameField?: string; // Field name in source grid for column names (default: "name")
  columnTypeField?: string; // Field name in source grid for column types (default: "type")
  staticColumns?: GridColumn[]; // Fixed columns that are always present
  showRowNumbers?: boolean;
  allowRowSelection?: boolean;
  showToolbar?: boolean;
  maxHeight?: string;
  minRows?: number;
  maxRows?: number;
  emptyText?: string;
  lockedColumns?: number;
}

export interface DynamicGridConfig extends ComponentConfig {
  type: 'dynamicGrid';
  additionalOptions: DynamicGridOptions;
}

// Supporting interfaces for grid and dropdown components
export interface GridColumn {
  id: string;
  label: string;
  type: 'text' | 'dropdown' | 'checkbox' | 'number' | 'readonly';
  width?: number;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  options?: DropdownOption[];
  allowClear?: boolean;
  searchable?: boolean;
  // Column visibility options
  defaultVisible?: boolean;  // Whether column is visible by default
  optional?: boolean;        // Whether column can be added/removed
  // Column resizing options
  resizable?: boolean;       // Whether column can be resized by dragging (default: true)
  minWidth?: number;         // Minimum width when resizing (default: 60)
  maxWidth?: number;         // Maximum width when resizing (default: undefined)
  // Default value options
  defaultValue?: any;        // Value considered as "empty/default" - column hidden if all cells have this value
  // Dynamic loading options (for dropdown columns)
  dynamicLoad?: boolean;     // Enable backend fetching for dropdown columns
  transformType?: string;    // Transform type for API call
  componentId?: string;      // Component ID for API call
}

export interface GridRow {
  id: string;
  [key: string]: any;
}

export interface DropdownOption {
  label: string;
  value: any;
  disabled?: boolean;
  description?: string;
}

// Type-safe helper functions for working with additionalOptions
export const getTextFieldOptions = (config: ComponentConfig): TextFieldOptions => 
  config.additionalOptions as TextFieldOptions || {};

export const getDateFieldOptions = (config: ComponentConfig): DateFieldOptions => 
  config.additionalOptions as DateFieldOptions || {};

export const getCheckboxOptions = (config: ComponentConfig): CheckboxOptions => 
  config.additionalOptions as CheckboxOptions || {};

export const getDropdownOptions = (config: ComponentConfig): DropdownOptions => 
  config.additionalOptions as DropdownOptions || {};

export const getMetadataDropdownOptions = (config: ComponentConfig): MetadataDropdownOptions => 
  config.additionalOptions as MetadataDropdownOptions || { metadataType: '' };

export const getSQLEditorOptions = (config: ComponentConfig): SQLEditorOptions => 
  config.additionalOptions as SQLEditorOptions || {};

export const getDataGridOptions = (config: ComponentConfig): DataGridOptions => 
  config.additionalOptions as DataGridOptions || { columns: [] };

export const getDynamicGridOptions = (config: ComponentConfig): DynamicGridOptions => 
  config.additionalOptions as DynamicGridOptions || { sourceGridId: '' };

// Validation rules
export interface ValidationRule {
  type: 'required' | 'minLength' | 'maxLength' | 'pattern' | 'custom' | 'uniqueInPipeline';
  value?: any;
  message: string;
}

// Wizard/Page structure - UI only
export interface PropertyPage {
  id: string;
  title: string;
  description?: string;
  components: ComponentConfig[];
  visible?: boolean;
  validation?: ValidationRule[];
}

// UI Configuration - no values, pure UI metadata
export interface PropertyUIConfig {
  transformName: string;
  transformType: string;
  description?: string;
  pages: PropertyPage[];
}

// Value Configuration - contains only values and defaults
export interface PropertyValueConfig {
  values: Record<string, any>;
  defaults?: Record<string, any>;
}

// Combined configuration (for backward compatibility)
export interface PropertiesPanelConfig extends PropertyUIConfig {
  values: Record<string, any>;
}

// Component change event
export interface ComponentChangeEvent {
  componentId: string;
  value: any;
  pageId: string;
}

// Validation result
export interface ValidationResult {
  isValid: boolean;
  errors: Record<string, string[]>;
}

// Value change event (from the new value manager)
export interface ValueChangeEvent {
  componentId: string;
  value: any;
  pageId: string;
}

// Property value manager configuration
export interface PropertyValueManagerConfig {
  initialValues?: Record<string, any>;
  pages: PropertyPage[];
  onChange?: (values: Record<string, any>) => void;
  onValidationChange?: (validation: Record<string, boolean>) => void;
} 